import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchCourse } from "@/lib/logicApi";
import { fetchTrackRecords } from "@/lib/academyProgress";
import { LogicImage, GeometricEye } from "./LogicImage";
import { useLogicUi } from "./logicUi";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { ResumeButton, PhaseProgress } from "@/components/academy/LessonNav";
import { BTN_FX, DISCIPLINE_THEME } from "@/lib/academyTheme";

export function phaseStatus(phase, records) {
  const slugs = phase.lessons.map((l) => l.slug);
  const recs = records.filter((r) => slugs.includes(r.lessonSlug));
  if (!recs.length) return "none";
  const states = recs.map((r) => r.learningStatus || "Encountered");
  if (states.includes("Requires Further Practice")) return "renew";
  const strong = recs.filter((r) => ["Practised", "Applied", "Demonstrated"].includes(r.learningStatus));
  if (recs.length === slugs.length && strong.length === slugs.length) return "done";
  if (recs.length === slugs.length) return "ready";
  return "active";
}

const ROMAN = ["I", "II", "III", "IV", "V", "VI"];

export default function LogicCourse() {
  const [course, setCourse] = useState(null);
  const { ui, lang, L, t } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const [records, setRecords] = useState([]);
  const [threshold, setThreshold] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    fetchCourse().then(setCourse).catch(() => {});
    fetchTrackRecords("logic").then(setRecords);
  }, []);

  useSEO({
    title: "Stoic Logic | The Hall of Clear Judgment | StoicOs.ai Academy",
    description: "Learn to examine what appears before deciding what deserves your assent. An interactive Academy leergang in six phases.",
    canonical: langCanonical("/academy/logic", lang),
    alternates: langAlternates("/academy/logic"),
    og: { title: "Stoic Logic: The Hall of Clear Judgment", description: "Examine what appears before deciding what deserves your assent.", image: "https://stoicos.ai/academy/logic/logic-01-hall-of-clear-judgment.jpg", imageType: "image/jpeg", type: "article" },
  });

  if (!course) return <div className="min-h-screen bg-ink" />;
  const media = course.media || {};
  const returning = records.length > 0;
  const last = returning ? [...records].sort((a, b) => (b.lastActivityAt || "").localeCompare(a.lastActivityAt || ""))[0] : null;
  const allLessons = course.phases.flatMap((p) => p.lessons.map((l) => ({ ...l, phaseSlug: p.slug })));
  const lastLesson = last ? allLessons.find((l) => l.slug === last.lessonSlug) : null;
  const firstLesson = allLessons[0];
  const continueTo = lastLesson ? `/academy/logic/phase/${lastLesson.phaseSlug}/lesson/${lastLesson.slug}` : `/academy/logic/phase/${firstLesson.phaseSlug}/lesson/${firstLesson.slug}`;

  if (threshold) {
    return (
      <main className="min-h-screen bg-ink flex items-center justify-center px-6 py-24">
        <div className="max-w-2xl w-full text-center space-y-10">
          <div className="max-w-lg mx-auto"><LogicImage media={media} assetKey="logic-09-threshold" /></div>
          <p className="font-serif text-2xl md:text-3xl text-bone leading-snug">{L(course.thresholdLine)}</p>
          <button
            data-testid="logic-begin-first-practice"
            onClick={() => nav(course.access === "preview" ? "/my-stoicos" : `/academy/logic/phase/${firstLesson.phaseSlug}/lesson/${firstLesson.slug}`)}
            className={`min-h-[52px] px-10 py-4 text-xs font-semibold tracking-[0.25em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}
          >
            {course.access === "preview" ? t("trial.cta") : ui.beginFirst}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-ink" data-testid="logic-course-page">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <LogicImage media={media} assetKey="logic-01-hall-of-clear-judgment" ratio="" className="absolute inset-0" alt="The Hall of Clear Judgment" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 pb-16 pt-32 w-full">
          <DisciplineSwitcher active="logic" />
          <div className="flex items-center gap-4 mb-6"><GeometricEye media={media} size={44} /><span className="text-[10px] tracking-[0.35em] uppercase text-[#8fa3c8]">{ui.discipline}</span></div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone tracking-tight uppercase">{L(course.title)}</h1>
          <p className="mt-3 font-serif italic text-xl md:text-2xl text-gold/90">{L(course.hallName)}</p>
          <p className="mt-6 max-w-2xl text-fog font-reading leading-[1.9]">{L(course.introduction)}</p>
          <p className="mt-3 max-w-2xl text-fog/80 font-reading">{L(course.heroIntro)}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="logic-enter-hall" onClick={() => setThreshold(true)}
              className={`min-h-[52px] px-8 py-4 text-xs font-semibold tracking-[0.22em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>{ui.enterHall}</button>
            {returning && <ResumeButton discipline="logic" course={course} records={records} />}
          </div>
        </div>
      </section>

      {/* Core movement */}
      <section className="border-t border-white/5 py-16 px-6 sm:px-10">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <p className="font-serif text-2xl md:text-3xl text-bone">{(course.coreMovement || []).map((m) => L(m)).join("  →  ")}</p>
          <p className="text-xs tracking-[0.22em] uppercase text-[#8fa3c8]">{(course.fullMovement || []).map((m) => L(m)).join(" · ")}</p>
        </div>
      </section>

      {/* Six-phase map */}
      <section className="border-t border-white/5 py-20 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto border border-[#8fa3c8]/45 shadow-[0_0_28px_rgba(143,163,200,0.22)] rounded-sm p-6 md:p-10" data-testid="logic-course-frame">
          <h2 className="font-serif text-2xl md:text-3xl text-bone mb-12 text-center">{ui.sixPhases}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {course.phases.map((p) => {
              const st = phaseStatus(p, records);
              return (
                <div key={p.slug} className="group border border-[#c8a95a]/20 bg-[#171c26] hover:border-gold/50 transition-colors p-8 flex flex-col gap-4">
                  <h3 className="font-serif text-xl text-bone">{ROMAN[p.order - 1]} · {L(p.title)}</h3>
                  <div className="flex items-center justify-between gap-3">
                    <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${st === "done" ? "border-gold/60 text-gold" : st === "none" ? "border-slate-500/40 text-slate-400" : "border-[#8fa3c8]/50 text-[#b9c6de]"}`}>{ui.status[st]}</span>
                    <PhaseProgress phase={p} records={records} accent="#8fa3c8" />
                  </div>
                  <Link to={`/academy/logic/phase/${p.slug}`} data-testid={`logic-phase-card-${p.slug}`}
                    className={`inline-flex items-center justify-center gap-2 min-h-[48px] px-5 text-[11px] font-semibold tracking-[0.2em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
                    {t("academy.nav.openPhase")} <span aria-hidden>→</span>
                  </Link>
                  <p className="text-sm text-fog/80 font-reading leading-relaxed">{L(p.centralQuestion)}</p>
                  <p className="text-xs text-[#8fa3c8] mt-auto pt-2 border-t border-white/5">{L(p.coreAbility)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Returning learner continuation */}
      {returning && lastLesson && (
        <section className="border-t border-white/5 py-16 px-6 sm:px-10">
          <div className="max-w-3xl mx-auto border border-[#c8a95a]/20 bg-[#171c26] p-8">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8fa3c8] mb-4">{ui.practiceContinues}</p>
            <p className="font-serif text-lg text-bone">{ui.lastActive} {L(lastLesson.title)}</p>
            <div className="mt-6 flex flex-wrap gap-4 text-xs tracking-[0.18em] uppercase">
              <Link to={course.access === "preview" ? "/my-stoicos" : continueTo} className="text-gold hover:underline" data-testid="logic-resume-link">{course.access === "preview" ? t("trial.cta") : ui.resumeLesson}</Link>
              <Link to="/academy/logic/journal" className="text-[#b9c6de] hover:underline">{ui.recordOfJudgment}</Link>
              <Link to="/academy/logic/practice" className="text-[#b9c6de] hover:underline">{ui.practiceBeyond}</Link>
              <Link to="/academy/logic/progress" className="text-[#b9c6de] hover:underline">{ui.pathOfPractice}</Link>
              <Link to="/academy/my-work" className="text-[#b9c6de] hover:underline" data-testid="logic-mywork-link">{t("academy.nav.myWork")}</Link>
              <Link to="/academy/logic/terms" className="text-[#b9c6de] hover:underline">{ui.theTerms}</Link>
            </div>
          </div>
        </section>
      )}

      {/* Chamber preview (locked) */}
      <section className="border-t border-white/5 py-16 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto flex items-center gap-6">
          <div className="w-28 shrink-0 opacity-60"><LogicImage media={media} assetKey="logic-11-chamber-of-judgment" ratio="aspect-[3/2]" alt="" /></div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8fa3c8]">{ui.finalAssessment}</p>
            <p className="font-serif text-lg text-bone mt-1">{ui.chamberName}</p>
            <p className="text-sm text-fog/70 mt-1">{ui.chamberOpens}</p>
          </div>
        </div>
      </section>
    </main>
  );
}
